import { useState, useEffect, useRef } from "react";
import TeamLogo from "./TeamLogo";

const SPORT_ICONS = { football: "⚽", nba: "🏀", mlb: "⚾", tennis: "🎾" };
const SPORT_LABELS = { football: "Fútbol", nba: "NBA", mlb: "MLB", tennis: "Tenis" };

// ─── Parse "MM:SS" or "MM:SS+OT:SS" into total seconds ───────────────────────
function parseClockToSecs(clockStr) {
  if (!clockStr) return null;
  const main = clockStr.split("+")[0];
  const parts = main.split(":");
  const mins = parseInt(parts[0] || "0", 10);
  const secs = parseInt(parts[1] || "0", 10);
  if (isNaN(mins) || isNaN(secs)) return null;
  return mins * 60 + secs;
}

// ─── Live Clock Hook: real-time ticker between API refreshes ──────────────────
function useLiveClock(match) {
  const [tick, setTick] = useState(0);
  const prevApiClock = useRef(match.display_clock);

  // Reset tick when API sends new clock value
  useEffect(() => {
    if (match.display_clock !== prevApiClock.current) {
      prevApiClock.current = match.display_clock;
      setTick(0);
    }
  }, [match.display_clock]);

  // Tick every second when live
  useEffect(() => {
    const isLive = match.status === "in" || match.is_live;
    if (!isLive) return;
    const timer = setInterval(() => setTick(t => t + 1), 1000);
    return () => clearInterval(timer);
  }, [match.status, match.is_live]);

  const isLive = match.status === "in" || match.is_live === true;
  if (!isLive) return null;

  const sport = match.sport;
  const period = match.period || 1;
  const isHalftime = match.status_desc === "Halftime" || match.status_desc === "Half Time";

  // ── Football ──────────────────────────────────────────────────────
  if (sport === "football") {
    if (isHalftime) {
      return { label: "PT", sub: "Primer Tiempo", color: "#D4AF37", isHalftime: true };
    }
    const baseSecs = parseClockToSecs(match.display_clock);
    if (baseSecs !== null) {
      const total = baseSecs + tick;
      const m = Math.floor(total / 60);
      const s = total % 60;
      const displayMin = Math.min(m, period === 1 ? 45 : 90);
      const extraMin = m - displayMin;
      const clockStr = extraMin > 0
        ? `${displayMin}+${extraMin}:${String(s).padStart(2, "0")}'`
        : `${displayMin}:${String(s).padStart(2, "0")}'`;
      const isSecondHalf = period >= 2;
      return { label: clockStr, sub: isSecondHalf ? "2° Tiempo" : "1° Tiempo", color: "#10B981" };
    }
    return { label: "EN VIVO", sub: period >= 2 ? "2° Tiempo" : "1° Tiempo", color: "#10B981" };
  }

  // ── NBA ───────────────────────────────────────────────────────────
  if (sport === "nba") {
    const baseSecs = parseClockToSecs(match.display_clock);
    if (baseSecs !== null) {
      const remaining = Math.max(0, baseSecs - tick);
      const m = Math.floor(remaining / 60);
      const s = remaining % 60;
      const quarterLabel = period <= 4 ? `Q${period}` : `OT${period - 4}`;
      const clockStr = `${quarterLabel} ${m}:${String(s).padStart(2, "0")}`;
      return { label: clockStr, sub: period <= 4 ? `Cuarto ${period}` : "Tiempo Extra", color: "#10B981" };
    }
    const quarterLabel2 = period <= 4 ? `Cuarto ${period}` : "Tiempo Extra";
    return { label: `Q${period}`, sub: quarterLabel2, color: "#10B981" };
  }

  // ── MLB ───────────────────────────────────────────────────────────
  if (sport === "mlb") {
    const inningLabel = period > 0 ? `Inn. ${period}` : "EN VIVO";
    return { label: inningLabel, sub: `Entrada ${period}`, color: "#10B981" };
  }

  return { label: "EN VIVO", sub: "", color: "#10B981" };
}

// ─── Smart team name shortener ───────────────────────────────────────────────
const SUFFIXES = /\b(FC|CF|SC|SD|AC|AS|AFC|BC|RFC|SL|BV|UD|CD|RB|1\.?FC|TSG)\b\.?$/i;
const PREFIXES = /^(FC|CF|AFC|AC|AS|RB|1\.?FC|TSG)\s+/i;
function shortTeam(name = "") {
  const clean = name.replace(SUFFIXES, "").replace(PREFIXES, "").trim();
  // If still long, take first 2 words max
  const words = clean.split(/\s+/);
  if (words.length <= 2) return clean;
  // Special: "Club Atlético de Madrid" → "Atlético"
  const skip = ["club", "real", "de", "del", "el", "la", "los", "las", "sporting"];
  const meaningful = words.filter(w => !skip.includes(w.toLowerCase()));
  return meaningful.slice(0, 2).join(" ") || words.slice(0, 2).join(" ");
}

// ─── Live Match Card (broadcast/vertical style for the live grid) ─────────────
export function LiveMatchCard({ match }) {
  const liveClock = useLiveClock(match);
  const leagueLabel = match.league || SPORT_LABELS[match.sport] || match.sport || "";

  return (
    <div
      data-testid="live-match-card"
      className="card-depth rounded-2xl overflow-hidden flex flex-col"
      style={{
        border: "1px solid rgba(16,185,129,0.3)",
        background: "linear-gradient(160deg, rgba(16,185,129,0.04) 0%, rgba(10,10,12,0) 60%)",
        boxShadow: "0 0 20px -8px rgba(16,185,129,0.18)",
      }}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-3 pt-2.5 pb-1">
        <span className="text-[10px] font-black tracking-wider uppercase truncate" style={{ color: "#555", maxWidth: "55%" }}>
          {SPORT_ICONS[match.sport]} {leagueLabel}
        </span>
        {/* VIVO pulsante */}
        <span
          className="text-[11px] font-black flex items-center gap-1"
          style={{ color: "#10B981", animation: "vivo-pulse 1.4s ease-in-out infinite" }}
          data-testid="live-vivo-badge"
        >
          <span className="w-1.5 h-1.5 rounded-full inline-block" style={{ background: "#10B981" }} />
          {liveClock?.isHalftime ? "PT" : liveClock?.label ? liveClock.label : "VIVO"}
        </span>
      </div>

      {/* Scoreboard: Home — Score — Away */}
      <div className="flex items-center justify-between gap-1 px-3 py-3">
        {/* Home */}
        <div className="flex flex-col items-center gap-1.5 flex-1 min-w-0">
          <TeamLogo logoUrl={match.home_logo} teamName={match.home_team} size={36} rounded="md" />
          <span className="text-[11px] font-bold text-center leading-tight text-white" style={{ maxWidth: 70, display: "block", wordBreak: "break-word" }}>
            {shortTeam(match.home_team)}
          </span>
        </div>

        {/* Score */}
        <div className="flex flex-col items-center flex-shrink-0 px-1">
          <div className="flex items-center gap-1.5">
            <span className="text-[22px] font-black tabular-nums text-white leading-none">
              {match.home_score ?? "—"}
            </span>
            <span className="text-[13px] font-black" style={{ color: "#3F3F46" }}>:</span>
            <span className="text-[22px] font-black tabular-nums text-white leading-none">
              {match.away_score ?? "—"}
            </span>
          </div>
          {liveClock?.sub && !liveClock.isHalftime && (
            <span className="text-[9px] font-bold mt-0.5 uppercase tracking-wider" style={{ color: "#10B981" }}>
              {liveClock.sub}
            </span>
          )}
        </div>

        {/* Away */}
        <div className="flex flex-col items-center gap-1.5 flex-1 min-w-0">
          <TeamLogo logoUrl={match.away_logo} teamName={match.away_team} size={36} rounded="md" />
          <span className="text-[11px] font-bold text-center leading-tight text-white" style={{ maxWidth: 70, display: "block", wordBreak: "break-word" }}>
            {shortTeam(match.away_team)}
          </span>
        </div>
      </div>
    </div>
  );
}

// ─── Compact Card (2-column grid, for all matches including live) ─────────────
export function CompactMatchCard({ match }) {
  const isFinal = match.status === "post";
  const isLive  = match.status === "in" || match.is_live;
  const leagueLabel = match.league || SPORT_LABELS[match.sport] || match.sport || "";
  const liveClock = useLiveClock(match);

  // Time until match
  let timeLabel = null;
  if (!isLive && !isFinal) {
    const matchDate = match.match_date || match.commence_time;
    if (matchDate) {
      const diff = (new Date(matchDate) - new Date()) / 3600000;
      if (diff < 1 / 60) timeLabel = "Ahora";
      else if (diff < 1) timeLabel = `${Math.round(diff * 60)}min`;
      else if (diff < 24) timeLabel = `${Math.round(diff)}h`;
      else timeLabel = new Date(matchDate).toLocaleDateString("es-ES", { weekday: "short", hour: "2-digit", minute: "2-digit" });
    }
  }

  return (
    <div
      data-testid="compact-match-card"
      className="card-depth rounded-xl overflow-hidden flex flex-col"
      style={{
        border: isLive ? "1px solid rgba(16,185,129,0.3)" : "1px solid #27272A",
        background: isLive ? "rgba(16,185,129,0.02)" : undefined,
        boxShadow: isLive ? "0 0 16px -6px rgba(16,185,129,0.15)" : undefined,
      }}
    >
      {/* Header: league + status */}
      <div className="flex items-center justify-between px-2.5 pt-2 pb-1.5">
        <span className="text-[10px] font-black tracking-wider uppercase truncate" style={{ color: "#666", maxWidth: "60%" }}>
          {SPORT_ICONS[match.sport]} {leagueLabel}
        </span>
        {isLive ? (
          <div className="flex items-center gap-1">
            <span className="w-1.5 h-1.5 rounded-full animate-pulse flex-shrink-0" style={{ background: "#10B981" }} />
            {liveClock ? (
              <span className="text-[10px] font-black tabular-nums" style={{ color: "#10B981", animation: "vivo-pulse 1.4s ease-in-out infinite" }}>
                {liveClock.isHalftime ? "PT" : liveClock.label}
              </span>
            ) : (
              <span className="text-[10px] font-black" style={{ color: "#10B981", animation: "vivo-pulse 1.4s ease-in-out infinite" }}>VIVO</span>
            )}
          </div>
        ) : isFinal ? (
          <span className="text-[10px] font-black" style={{ color: "#555" }}>FINAL</span>
        ) : timeLabel ? (
          <span className="text-[10px] font-semibold" style={{ color: "#888" }}>{timeLabel}</span>
        ) : null}
      </div>

      {/* Teams */}
      <div className="px-2.5 pb-2.5 flex flex-col gap-1.5">
        {/* Home */}
        <div className="flex items-center justify-between gap-1.5">
          <div className="flex items-center gap-1.5 min-w-0">
            <TeamLogo logoUrl={match.home_logo} teamName={match.home_team} size={22} />
            <span className="text-[13px] font-bold leading-tight truncate" style={{ color: "#fff" }}>
              {shortTeam(match.home_team)}
            </span>
          </div>
          {(isLive || isFinal) && (
            <span className="text-[15px] font-black flex-shrink-0" style={{ color: isFinal ? "#A1A1AA" : "#fff", fontVariantNumeric: "tabular-nums" }}>
              {match.home_score ?? "—"}
            </span>
          )}
        </div>
        {/* Away */}
        <div className="flex items-center justify-between gap-1.5">
          <div className="flex items-center gap-1.5 min-w-0">
            <TeamLogo logoUrl={match.away_logo} teamName={match.away_team} size={22} />
            <span className="text-[13px] font-bold leading-tight truncate" style={{ color: "#bbb" }}>
              {shortTeam(match.away_team)}
            </span>
          </div>
          {(isLive || isFinal) && (
            <span className="text-[15px] font-black flex-shrink-0" style={{ color: isFinal ? "#A1A1AA" : "#fff", fontVariantNumeric: "tabular-nums" }}>
              {match.away_score ?? "—"}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Full Card ────────────────────────────────────────────────────────────────
function TimeLabel({ match }) {
  const matchDate = match.match_date || match.commence_time;
  if (!matchDate) return null;
  const now = new Date();
  const date = new Date(matchDate);
  const hoursUntil = (date - now) / 3600000;
  if (hoursUntil < 0) return null;
  let label;
  if (hoursUntil < 1 / 60) label = "Ahora";
  else if (hoursUntil < 1) label = `${Math.round(hoursUntil * 60)}min`;
  else if (hoursUntil < 24) label = `${Math.round(hoursUntil)}h`;
  else label = date.toLocaleDateString("es-ES", { weekday: "short", hour: "2-digit", minute: "2-digit" });
  return <span className="text-[11px] font-semibold" style={{ color: "#888" }}>{label}</span>;
}

function ScoreBox({ score, isFinal }) {
  return (
    <div
      className="flex items-center justify-center rounded-lg flex-shrink-0"
      style={{
        background: isFinal ? "transparent" : "#0A0A0C",
        border: isFinal ? "none" : "1px solid rgba(255,255,255,0.1)",
        minWidth: 40, height: 40
      }}
    >
      <span
        className="text-xl font-black leading-none"
        style={{ color: isFinal ? "#A1A1AA" : "#fff", fontVariantNumeric: "tabular-nums" }}
      >
        {score ?? "—"}
      </span>
    </div>
  );
}

function OddsButton({ label, odds, selected, onClick }) {
  return (
    <button onClick={onClick}
      className="flex-1 flex flex-col items-center justify-center gap-0.5 py-3 rounded-xl transition-all duration-150"
      style={selected
        ? { background: "rgba(212,175,55,0.15)", border: "1.5px solid #D4AF37" }
        : { background: "#14141C", border: "1px solid rgba(255,255,255,0.08)" }
      }>
      <span className="text-[10px] font-semibold" style={{ color: "#888" }}>{label}</span>
      <span className="text-sm font-black" style={{ color: "#D4AF37" }}>
        {typeof odds === "number" ? odds.toFixed(2) : odds}
      </span>
    </button>
  );
}

// ─── Main MatchCard ───────────────────────────────────────────────────────────
export default function MatchCard({ match, selectedOdd, onSelectOdd }) {
  const isLive = match.status === "in" || match.is_live === true;
  const isFinal = match.status === "post";
  const showScore = isLive || isFinal;
  const liveClock = useLiveClock(match);
  const homeOdds = match.home_odds?.[0]?.odds;
  const awayOdds = match.away_odds?.[0]?.odds;
  const drawOdds = match.draw_odds?.[0]?.odds;
  const leagueLabel = match.league || SPORT_LABELS[match.sport] || match.sport || "";

  return (
    <div
      data-testid="match-card"
      className="card-depth w-full rounded-2xl overflow-hidden"
      style={{
        border: isLive
          ? "1px solid rgba(16,185,129,0.2)"
          : "1px solid #27272A",
      }}
    >
      {/* ── Header ── */}
      <div
        className="flex items-center justify-between px-4 py-2.5"
        style={{
          borderBottom: "1px solid rgba(255,255,255,0.05)",
          background: isLive ? "rgba(16,185,129,0.04)" : "rgba(0,0,0,0.2)",
        }}
      >
        <div className="flex items-center gap-1.5">
          <span className="text-xs">{SPORT_ICONS[match.sport] || "🏆"}</span>
          <span className="text-[11px] font-black tracking-wider uppercase" style={{ color: "#aaa" }}>
            {leagueLabel}
          </span>
        </div>

        {/* Live clock OR time until OR FINAL badge */}
        {isLive && liveClock ? (
          <div className="flex items-center gap-1.5">
            {liveClock.isHalftime ? (
              <span
                className="text-[11px] font-black px-2 py-0.5 rounded-full"
                style={{ background: "rgba(212,175,55,0.2)", color: "#D4AF37", border: "1px solid rgba(212,175,55,0.4)" }}
                data-testid="halftime-badge"
              >
                {liveClock.label}
              </span>
            ) : (
              <>
                <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: liveClock.color }} />
                <span className="text-[12px] font-black tabular-nums" style={{ color: liveClock.color }} data-testid="live-clock">
                  {liveClock.label}
                </span>
              </>
            )}
          </div>
        ) : isLive ? (
          <div className="flex items-center gap-1">
            <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: "#10B981" }} />
            <span className="text-[11px] font-black" style={{ color: "#10B981" }}>EN VIVO</span>
          </div>
        ) : match.status === "post" ? (
          <span className="text-[11px] font-black px-2 py-0.5 rounded" style={{ background: "rgba(255,255,255,0.05)", color: "#71717A", border: "1px solid #27272A" }}>
            FINAL
          </span>
        ) : (
          <TimeLabel match={match} />
        )}
      </div>

      {/* ── Period badge (below header, only for live) ── */}
      {isLive && liveClock?.sub && !liveClock.isHalftime && (
        <div
          className="px-4 py-1 text-[10px] font-bold"
          style={{ background: "rgba(16,185,129,0.04)", color: "#10B981", borderBottom: "1px solid rgba(255,255,255,0.04)" }}
        >
          {liveClock.sub}
        </div>
      )}

      {/* ── Teams ── */}
      <div>
        {/* Home */}
        <div
          className="flex items-center gap-3 px-4 py-3.5"
          style={{ borderBottom: "1px solid rgba(255,255,255,0.04)" }}
        >
          <TeamLogo logoUrl={match.home_logo} teamName={match.home_team} size={40} />
          <div className="flex-1 min-w-0">
            <span className="text-[15px] font-black block truncate" style={{ color: "#fff", letterSpacing: "-0.2px" }}>
              {match.home_team}
            </span>
          </div>
          {showScore && <ScoreBox score={match.home_score} isFinal={isFinal} />}
        </div>

        {/* Away */}
        <div className="flex items-center gap-3 px-4 py-3.5">
          <TeamLogo logoUrl={match.away_logo} teamName={match.away_team} size={40} />
          <div className="flex-1 min-w-0">
            <span className="text-[15px] font-black block truncate" style={{ color: "#ddd", letterSpacing: "-0.2px" }}>
              {match.away_team}
            </span>
          </div>
          {showScore && <ScoreBox score={match.away_score} isFinal={isFinal} />}
        </div>
      </div>

      {/* ── Odds ── */}
      {(homeOdds || awayOdds) && (
        <div
          className="flex gap-2 px-4 pb-3.5 pt-3"
          style={{ borderTop: "1px solid rgba(255,255,255,0.04)" }}
        >
          {homeOdds && (
            <OddsButton label={match.home_team?.split(" ").slice(-1)[0]}
              odds={homeOdds} selected={selectedOdd === `${match.id}_home`}
              onClick={() => onSelectOdd?.(`${match.id}_home`)} />
          )}
          {drawOdds && (
            <OddsButton label={match.sport === "nba" ? "Spread" : "Empate"}
              odds={drawOdds} selected={selectedOdd === `${match.id}_draw`}
              onClick={() => onSelectOdd?.(`${match.id}_draw`)} />
          )}
          {awayOdds && (
            <OddsButton label={match.away_team?.split(" ").slice(-1)[0]}
              odds={awayOdds} selected={selectedOdd === `${match.id}_away`}
              onClick={() => onSelectOdd?.(`${match.id}_away`)} />
          )}
        </div>
      )}
    </div>
  );
}
